import api from './api';
import { API_ENDPOINTS, USE_MOCK_DATA } from '../config/api';
import { getMockData, delay } from './mock-data-loader';

export async function getMatchById(matchId) {
  if (USE_MOCK_DATA) {
    const { mockMatches, mockTeams } = await getMockData();
    await delay();
    const match = mockMatches.find(m => m.id === matchId);
    if (!match) {
      throw new Error('Match not found');
    }

    // Enhance with team data
    const team1 = mockTeams.find(t => t.id === match.team1Id);
    const team2 = mockTeams.find(t => t.id === match.team2Id);

    return {
      ...match,
      team1: team1 ? { id: team1.id, name: team1.name } : null,
      team2: team2 ? { id: team2.id, name: team2.name } : null,
    };
  }

  const response = await fetch(API_ENDPOINTS.matches(matchId));
  if (!response.ok) {
    throw new Error('Failed to fetch match');
  }
  return response.json();
}

/**
 * Get match details with team names and player rosters (authenticated)
 */
export async function getMatchDetails(matchId) {
  if (USE_MOCK_DATA) {
    const { mockMatches, mockTeams, mockPlayers } = await getMockData();
    await delay();
    const match = mockMatches.find(m => m.id === matchId);
    if (!match) {
      throw new Error('Match not found');
    }
    const team1 = mockTeams.find(t => t.id === match.team1Id);
    const team2 = mockTeams.find(t => t.id === match.team2Id);
    const mapPlayers = (players) => players.map(p => ({
      playerId: p.id || p.playerId,
      firstName: p.firstName || p.name?.split(' ')[0] || '',
      lastName: p.lastName || p.name?.split(' ').slice(1).join(' ') || '',
      isActive: true,
    }));
    return {
      match: { ...match, matchId: match.id, team1Name: team1?.name, team2Name: team2?.name },
      team1Players: mapPlayers(mockPlayers.filter(p => team1?.playerIds?.includes(p.id)) || []),
      team2Players: mapPlayers(mockPlayers.filter(p => team2?.playerIds?.includes(p.id)) || []),
    };
  }

  return api.get(`/matches/${matchId}`);
}

export async function getCompetitionMatches(competitionId) {
  if (USE_MOCK_DATA) {
    const { mockMatches, mockTeams } = await getMockData();
    await delay();
    const matches = mockMatches.filter(m => m.competitionId === competitionId);
    
    // Enhance with team data
    return matches.map(match => {
      const team1 = mockTeams.find(t => t.id === match.team1Id);
      const team2 = mockTeams.find(t => t.id === match.team2Id);
      
      return {
        ...match,
        team1: team1 ? { id: team1.id, name: team1.name } : null,
        team2: team2 ? { id: team2.id, name: team2.name } : null,
      };
    });
  }

  const response = await fetch(`${API_ENDPOINTS.competitions}/${competitionId}/matches`);
  if (!response.ok) {
    throw new Error('Failed to fetch matches');
  }
  return response.json();
}

export async function submitMatchResults(matchId, results) {
  if (USE_MOCK_DATA) {
    await delay(800);
    console.log('Mock: Submitting match results', { matchId, results });
    return { success: true, message: 'Results submitted successfully (mock)' };
  }

  const response = await fetch(API_ENDPOINTS.submitResults(matchId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(results),
  });
  
  if (!response.ok) {
    throw new Error('Failed to submit match results');
  }
  return response.json();
}

export async function approveMatchResults(matchId, approved, notes = null) {
  if (USE_MOCK_DATA) {
    await delay(600);
    console.log('Mock: Approving match results', { matchId, approved, notes });
    return { success: true, message: 'Results approved successfully (mock)' };
  }

  const response = await fetch(API_ENDPOINTS.approveResults(matchId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ approved, notes }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to approve match results');
  }
  return response.json();
}

export async function getPendingMatches(competitionId = null) {
  if (USE_MOCK_DATA) {
    await delay();
    // Return matches awaiting approval
    return { matches: [] };
  }

  const params = competitionId ? `?competitionId=${competitionId}` : '';
  const response = await fetch(`${API_ENDPOINTS.pendingMatches}${params}`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch pending matches');
  }
  return response.json();
}

export async function getScheduledMatches(teamId) {
  if (USE_MOCK_DATA) {
    const { mockMatches, mockTeams } = await getMockData();
    await delay();
    const matches = mockMatches
      .filter(m =>
        (m.team1Id === teamId || m.team2Id === teamId) &&
        m.status === 'scheduled'
      )
      .map(match => {
        const team1 = mockTeams.find(t => t.id === match.team1Id);
        const team2 = mockTeams.find(t => t.id === match.team2Id);

        return {
          ...match,
          team1: team1 ? { id: team1.id, name: team1.name } : null,
          team2: team2 ? { id: team2.id, name: team2.name } : null,
        };
      });

    return { matches };
  }

  return api.get(`/teams/${teamId}/matches?status=scheduled`);
}

export async function getCaptainMatches() {
  if (USE_MOCK_DATA) {
    const { mockMatches, mockTeams } = await getMockData();
    await delay();
    // Return upcoming and pending matches for captain
    const matches = mockMatches.map(match => {
      const team1 = mockTeams.find(t => t.id === match.team1Id);
      const team2 = mockTeams.find(t => t.id === match.team2Id);
      
      return {
        ...match,
        team1: team1 ? { id: team1.id, name: team1.name } : null,
        team2: team2 ? { id: team2.id, name: team2.name } : null,
      };
    });
    
    return {
      upcoming: matches.filter(m => m.status === 'scheduled'),
      pending: [],
      conflicts: [],
    };
  }

  const response = await fetch(API_ENDPOINTS.captainMatches);
  if (!response.ok) {
    throw new Error('Failed to fetch captain matches');
  }
  return response.json();
}

export default {
  getMatchById,
  getMatchDetails,
  getCompetitionMatches,
  submitMatchResults,
  approveMatchResults,
  getPendingMatches,
  getScheduledMatches,
  getCaptainMatches,
};
